import React, { useMemo, useState } from 'react';

import type {
  IContaDoPlano,
  IVerificacao,
  Severidade,
} from '../../../../../../hooks/useFechamentoContabilMensal';
import {
  agruparConstatacoes,
  brl,
  filtrarConstatacoes,
  ORDEM_SEVERIDADE,
  SEVERIDADE,
  textoDaConta,
} from '../../regras';
import { CABECA_DA_TABELA, CAIXA, pilula, SUB, TH, TITULO } from './estilos';

/** Aba Constatações do detalhe: as verificações do sistema na competência. */
export function AbaConstatacoes({
  itens,
  plano,
}: {
  itens: IVerificacao[];
  plano?: Record<string, IContaDoPlano>;
}) {
  const [marcadas, setMarcadas] = useState<Severidade[]>(ORDEM_SEVERIDADE);
  const [busca, setBusca] = useState('');
  const [abertos, setAbertos] = useState<string[]>([]);
  const visiveis = useMemo(
    () => filtrarConstatacoes(itens, marcadas, busca),
    [itens, marcadas, busca],
  );
  const grupos = useMemo(() => agruparConstatacoes(visiveis), [visiveis]);
  const contagem = (s: Severidade) => itens.filter(i => i.severidade === s).length;
  const alternar = (s: Severidade) =>
    setMarcadas(atual => (atual.includes(s) ? atual.filter(x => x !== s) : [...atual, s]));
  const abrir = (chave: string) =>
    setAbertos(atual =>
      atual.includes(chave) ? atual.filter(x => x !== chave) : [...atual, chave],
    );

  return (
    <>
      <div className="cz3">
        <div style={CAIXA}>
          <div style={TITULO}>Distribuição por gravidade</div>
          <span style={{ ...SUB, marginBottom: 4 }}>
            Verificações automáticas do sistema nesta competência.
          </span>
          {ORDEM_SEVERIDADE.map(s => {
            const n = contagem(s);
            const sev = SEVERIDADE[s];
            return (
              <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 92, flexShrink: 0 }}>
                  <span style={pilula(sev.suave, sev.cor)}>{sev.rotulo}</span>
                </span>
                <span
                  style={{
                    flex: 1,
                    height: 7,
                    borderRadius: 4,
                    background: '#EEF2F6',
                    overflow: 'hidden',
                  }}
                >
                  <span
                    style={{
                      display: 'block',
                      height: 7,
                      borderRadius: 4,
                      width: `${itens.length ? (n / itens.length) * 100 : 0}%`,
                      background: sev.cor,
                    }}
                  />
                </span>
                <b style={{ width: 24, textAlign: 'right', fontSize: 12.5 }}>{n}</b>
              </div>
            );
          })}
        </div>
        <div className="czSpan2" style={CAIXA}>
          <div style={TITULO}>Como ler</div>
          <span style={{ ...SUB, marginBottom: 4 }}>
            A gravidade segue a tabela do procedimento de revisão.
          </span>
          {ORDEM_SEVERIDADE.map(s => {
            const sev = SEVERIDADE[s];
            return (
              <div key={s} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                <span style={{ width: 92, flexShrink: 0 }}>
                  <span style={pilula(sev.suave, sev.cor)}>{sev.rotulo}</span>
                </span>
                <span style={{ fontSize: 12 }}>{sev.leitura}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 12,
          border: '1px solid #E4E4E7',
          background: 'rgba(255, 255, 255, .6)',
        }}
      >
        {ORDEM_SEVERIDADE.map(s => {
          const ativo = marcadas.includes(s);
          const sev = SEVERIDADE[s];
          return (
            <button
              type="button"
              key={s}
              onClick={() => alternar(s)}
              aria-pressed={ativo}
              style={{
                padding: '5px 12px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                border: `1.5px solid ${ativo ? sev.cor : '#E4E4E7'}`,
                background: ativo ? sev.suave : '#FFFFFF',
                color: ativo ? sev.cor : '#52525B',
              }}
            >
              {sev.rotulo} · {contagem(s)}
            </button>
          );
        })}
        <input
          aria-label="Buscar nas constatações"
          value={busca}
          onChange={ev => setBusca(ev.target.value)}
          placeholder="Buscar nas constatações"
          style={{
            marginLeft: 'auto',
            width: 280,
            maxWidth: '100%',
            height: 34,
            boxSizing: 'border-box',
            border: '1px solid #D4DAE2',
            borderRadius: 10,
            padding: '0 10px',
            font: 'inherit',
            fontSize: 12.5,
            background: '#FFFFFF',
          }}
        />
      </div>

      <div style={{ ...CAIXA, gap: 8 }}>
        <div style={TITULO}>
          Resumo por tipo · {visiveis.length} de {itens.length}
        </div>
        <span style={SUB}>Uma linha por tipo de constatação. Clique para ver os itens.</span>
        {grupos.length === 0 && (
          <p
            style={{
              margin: 0,
              padding: 20,
              textAlign: 'center',
              fontSize: 12.5,
              color: '#52525B',
            }}
          >
            Nenhuma constatação para os filtros atuais.
          </p>
        )}
        {grupos.length > 0 && (
          <div
            className="czScroll"
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex={0}
            role="region"
            aria-label="Lista de constatações"
            style={{ overflowX: 'auto' }}
          >
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 720 }}
            >
              <thead>
                <tr style={CABECA_DA_TABELA}>
                  <th style={{ ...TH, width: 140 }}>Gravidade</th>
                  <th style={TH}>Código e tipo</th>
                  <th style={{ ...TH, textAlign: 'right', width: 70 }}>Itens</th>
                  <th style={{ ...TH, textAlign: 'right', width: 160 }}>Valor somado</th>
                </tr>
              </thead>
              {grupos.map(g => {
                const aberto = abertos.includes(g.chave);
                const sev = SEVERIDADE[g.pior];
                return (
                  <tbody key={g.chave}>
                    <tr className="consTipo" style={{ borderTop: '1px solid #E4E4E7' }}>
                      <td style={{ padding: '9px 10px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#52525B"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            style={{ transform: `rotate(${aberto ? 90 : 0}deg)` }}
                          >
                            <path d="M9 6l6 6-6 6" />
                          </svg>
                          <span style={pilula(sev.suave, sev.cor)}>{sev.rotulo}</span>
                        </span>
                      </td>
                      <td style={{ padding: '9px 10px' }}>
                        <button
                          type="button"
                          onClick={() => abrir(g.chave)}
                          aria-expanded={aberto}
                          style={{
                            border: 0,
                            background: 'transparent',
                            padding: 0,
                            font: 'inherit',
                            fontWeight: 600,
                            color: '#0F172A',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          {g.chave}
                        </button>
                      </td>
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>{g.itens.length}</td>
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                        {g.total ? brl(g.total) : '—'}
                      </td>
                    </tr>
                    {aberto &&
                      g.itens.map((item, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={i} style={{ background: 'rgba(15, 23, 42, .025)' }}>
                          <td aria-hidden="true" />
                          <td style={{ padding: '8px 10px', fontSize: 12 }}>
                            {item.detalhe}
                            {item.contas.length > 0 && (
                              <div style={{ fontSize: 11.5, color: '#52525B', marginTop: 3 }}>
                                {item.contas.length === 1 ? 'Conta: ' : 'Contas: '}
                                {item.contas.map(c => textoDaConta(c, plano)).join(' · ')}
                              </div>
                            )}
                          </td>
                          <td aria-hidden="true" />
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12 }}>
                            {item.valor != null ? brl(item.valor) : '—'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </div>
    </>
  );
}
